import {
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type Row as TableRow,
  type SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Fragment, useEffect, useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Pagination from 'react-bootstrap/Pagination';
import Row from 'react-bootstrap/Row';
import DateRangeFilter from './DateRangeFilter';
import FacilitiesFilter from './FacilitiesFilter';
import SelectFilter from './SelectFilter';
import dateRange from './filters/dateRange';
import facilityIds from './filters/facilityIds';
import startsWithString from './filters/startsWithString';
import TextFilter from './TextFilter';

// Common props for both client and server-side tables
export type BaseHtmlTableProps = {
  rows: any[];
  columns?;
  columnDefs?: any[];
  hiddenColumns?: string[];
  expanded?: boolean;
  enableRowSelection?: boolean | ((row: TableRow<any>) => boolean);
  isRowExpanded?(row): boolean;
  getExpansionRow?(row): JSX.Element;
  onRowSelectionChange?: (rows: any[]) => void;
  getRowClassName?: (row: any) => string;
  getCellClassName?: (row: any, columnId: string) => string;
  pageSizeOptions?: number[];
  loading?: boolean;
  initialColumnFilters?: ColumnFiltersState;
  initialSorting?: SortingState;
  // Initial one-based page when using internal pagination state
  page?: number;
  perPage?: number;
  // Shown in footer when set, e.g. Showing N of total
  total?: number;
  showPagination?: boolean;
  showSearch?: boolean;
};

export type BaseHtmlTableClientSideProps = BaseHtmlTableProps & {
  paginationMode?: 'client';
};

export type BaseHtmlTableServerSideProps = BaseHtmlTableProps & {
  paginationMode: 'server';
  totalPages?: number;

  /**
   * When all of these are set, the table does not keep its own pagination, sort, or filter state.
   * The parent maps them to API params (see buildServerTableFilterRecord).
   */
  serverPagination?: PaginationState;
  onServerPaginationChange?: OnChangeFn<PaginationState>;
  serverSorting?: SortingState;
  onServerSortingChange?: OnChangeFn<SortingState>;
  serverColumnFilters?: ColumnFiltersState;
  onServerColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
};

export type BaseHtmlTableComponentProps =
  | BaseHtmlTableClientSideProps
  | BaseHtmlTableServerSideProps;

type ColumnFilterMeta = {
  filterComponent: string;
}

export function buildOrderBySortString(sorting: SortingState, columnDefs: any[]): string {
  return (sorting || [])
    .map((cur) => {
      let sortKey = cur.id;
      const def = columnDefs.find(
        (d: any) => d?.id === sortKey || d?.accessorKey === sortKey
      );
      const meta = def?.meta as { sortName?: string | string[] } | undefined;
      if (meta?.sortName) {
        if (Array.isArray(meta.sortName)) {
          return meta.sortName.map((subKey) => (cur.desc ? `-${subKey}` : subKey)).join(',');
        }
        sortKey = meta.sortName;
      }
      return cur.desc ? `-${sortKey}` : sortKey;
    })
    .join(',');
}

/** Builds a flat record of page, perPage, orderBy, and column filter values for API mapping. */
export function buildServerTableFilterRecord(
  pagination: PaginationState,
  columnFilters: ColumnFiltersState,
  sorting: SortingState,
  columnDefs: any[]
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    page: pagination.pageIndex + 1,
    perPage: pagination.pageSize,
  };
  columnFilters.forEach((f) => {
    filters[f.id] = f.value;
  });
  const sortString = buildOrderBySortString(sorting, columnDefs);
  if (sortString.trim()) {
    filters.orderBy = sortString;
  }
  return filters;
}

// Checks if the server-side props are fully defined and controlled
function isServerTableControlled(p: BaseHtmlTableServerSideProps | undefined): boolean {
  if (p == null) {
    return false;
  }
  return (
    p.serverPagination != null &&
    p.onServerPaginationChange != null &&
    p.serverSorting != null &&
    p.onServerSortingChange != null &&
    p.serverColumnFilters != null &&
    p.onServerColumnFiltersChange != null
  );
}

const BaseHtmlTable = (props: BaseHtmlTableComponentProps) => {
  const { rows, columns, page, perPage } = props;

  // --- Server mode ---
  const isServerMode = props.paginationMode === 'server';
  const serverSideProps: BaseHtmlTableServerSideProps | undefined = isServerMode
    ? props
    : undefined;

  const serverControlled = useMemo(
    () => isServerTableControlled(serverSideProps),
    [
      serverSideProps?.serverPagination,
      serverSideProps?.onServerPaginationChange,
      serverSideProps?.serverSorting,
      serverSideProps?.onServerSortingChange,
      serverSideProps?.serverColumnFilters,
      serverSideProps?.onServerColumnFiltersChange,
    ]
  );

  // --- Client-side sort (overridden when serverControlled) ---
  const [internalSorting, setInternalSorting] = useState<SortingState>(props.initialSorting ?? []);
  const sorting = serverControlled ? serverSideProps!.serverSorting! : internalSorting;
  const setSorting: OnChangeFn<SortingState> = serverControlled
    ? serverSideProps!.onServerSortingChange!
    : setInternalSorting;

  // --- Row selection (same for both modes) ---
  const [rowSelection, setRowSelection] = useState({});

  // --- Column definitions (shared between client and server modes) ---
  // Use untyped columns coming in if we didn't get the column defs directly
  let columnDefs = props.columnDefs;
  const columnHelper = createColumnHelper();
  if (columnDefs == null) {
    columnDefs = columns.map((column) =>
      columnHelper.accessor(column.key, {
        cell: info => info.getValue() ?? '',
        header: () => column.name
      })
    );
  }

  // Add checkbox column when selection is enabled
  const selectionColumn = props.enableRowSelection ? [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => {
        const hasSelectableRows = table.getRowModel().rows.some((row) => row.getCanSelect());
        if (!hasSelectableRows) {
          return null;
        }
        return (
          <div className="form-check d-flex justify-content-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={table.getIsAllPageRowsSelected()}
              ref={(input) => {
                if (input != null) {
                  input.indeterminate =
                    table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected();
                }
              }}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          </div>
        );
      },
      cell: ({ row }) =>
        row.getCanSelect() ? (
          <div className="form-check d-flex justify-content-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          </div>
        ) : null,
      size: 30
    })
  ] : [];
  columnDefs = [...selectionColumn, ...columnDefs];

  // --- Client-side pagination (overridden when serverControlled) ---
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: (page || 1) - 1,
    pageSize: perPage || 1000,
  });
  const pagination = serverControlled ? serverSideProps!.serverPagination! : internalPagination;
  const setPagination: OnChangeFn<PaginationState> = serverControlled
    ? serverSideProps!.onServerPaginationChange!
    : setInternalPagination;

  // --- Client-side column filters (overridden when serverControlled) ---
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>(
    props.initialColumnFilters ?? []
  );
  const columnFilters = serverControlled ? serverSideProps!.serverColumnFilters! : internalColumnFilters;
  const setColumnFilters: OnChangeFn<ColumnFiltersState> = serverControlled
    ? serverSideProps!.onServerColumnFiltersChange!
    : setInternalColumnFilters;

  const columnVisibility = {};
  (props.hiddenColumns || []).forEach(c => columnVisibility[c] = false);

  // --- TanStack table instance ---
  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    filterFns: {
      facilityIds,
      dateRange,
      startsWithString
    },
    enableRowSelection: props.enableRowSelection,
    state: {
      sorting,
      expanded: props.expanded || undefined,
      columnVisibility,
      columnFilters,
      pagination,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: !isServerMode ? getPaginationRowModel() : undefined,
    getIsRowExpanded: props.isRowExpanded,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    defaultColumn: {
      size: Number.MAX_SAFE_INTEGER
    },
    pageCount: isServerMode ? props.totalPages : undefined,
    manualPagination: isServerMode,
    manualFiltering: isServerMode,
    manualSorting: isServerMode,
  });

  useEffect(() => {
    if (props.onRowSelectionChange) {
      const selectedRows = table
        .getSelectedRowModel()
        .flatRows.filter((row) => row.getCanSelect())
        .map((row) => row.original);
      props.onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, rows]);

  // --- Footer: page size options and pagination (shared between client and server modes) ---
  let pageSizeOptions = (props.pageSizeOptions || [100, 250, 500, 1000, 2500]);
  if (props.perPage && pageSizeOptions.indexOf(props.perPage) === -1) {
    pageSizeOptions.push(props.perPage);
    pageSizeOptions.sort((a,b) => a-b);
  }

  return (
    <div>
      <table className="table table-sm table-striped table-bordered">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{
                    width: header.getSize() == Number.MAX_SAFE_INTEGER ? 'auto' : header.getSize(),
                  }}>
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? 'cursor-pointer select-none'
                          : '',
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {{
                        asc: <><i className="fas fa-sort-amount-down-alt" />&nbsp;</>,
                        desc: <><i className="fas fa-sort-amount-down" />&nbsp;</>
                      }[header.column.getIsSorted() as string] ?? null}

                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {/* Table filters */}
          {table.getAllColumns().filter(c => c.getCanFilter()).length > 0 && table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <td key={`${header.id}-filter`}>
                  {(() => {
                    if (header.column.getCanFilter()) {
                      let filterComponent = (header.column.columnDef.meta as ColumnFilterMeta)?.filterComponent;
                      if (filterComponent == 'FacilityIdsFilter') {
                        return (<div><FacilitiesFilter column={header.column} table={table} /></div>);
                      }
                      if (filterComponent == 'DateRangeFilter') {
                        return (<div><DateRangeFilter column={header.column} table={table} initialSettings={header.column.columnDef.meta || {}} /></div>);
                      }
                      if (filterComponent == 'SelectFilter') {
                        const options = (header.column.columnDef.meta as any)?.filterOptions || [];
                        return (<div><SelectFilter column={header.column} table={table} options={options} /></div>);
                      }
                      return (<div><TextFilter column={header.column} table={table} /></div>);
                    }
                    return null;
                  })()}
                </td>
              ))}
            </tr>
          ))}

          {props.loading &&
            <tr>
              <td colSpan={table.getAllColumns().length} style={{padding: '2rem'}}>
                <div className="d-flex justify-content-center align-items-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </td>
            </tr>
          }

          {!props.loading && table.getRowModel().rows.map(row => (
            <Fragment key={row.id}>
              <tr className={props.getRowClassName?.(row.original)}>
                {row.getVisibleCells().map(cell => {
                  const meta = cell.column.columnDef.meta as any;
                  const cellStyle = meta?.cellStyle;
                  const staticCellClassName = meta?.cellClassName as string | undefined;
                  const dynamicCellClassName = props.getCellClassName?.(row.original, cell.column.id);
                  const cellClassName = [staticCellClassName, dynamicCellClassName].filter(Boolean).join(' ');
                  return (
                    <td key={cell.id} style={cellStyle} className={cellClassName}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
              {row.getIsExpanded() && props.getExpansionRow &&
                props.getExpansionRow(row)
              }
            </Fragment>
          ))}
        </tbody>
      </table>
      {props.showPagination !== false &&
        <div className="mt-3">
          {/* Pagination controls */}
          {table.getPageCount() > 1 &&
            <span>
              <Pagination className="pull-left">
                <Pagination.First
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()} />
                <Pagination.Prev
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()} />
                <Pagination.Next
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()} />
                <Pagination.Last
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()} />
              </Pagination>
              <Badge bg="secondary" style={{ marginLeft: 5, marginTop: 8 }}>
                Page {table.getState().pagination.pageIndex+1} of {table.getPageCount()}
              </Badge>
            </span>
          }
          {/* Showing and total entries */}
          {props.total &&
            <span>
              <Badge bg="secondary" style={{ marginLeft: 5, marginTop: 8 }}>
                Showing {table.getRowCount()} of {props.total}
              </Badge>
            </span>
          }
          {/* Per-page selector */}
          <ButtonToolbar className="pull-right">
            <InputGroup>
              <Form.Group as={Row} style={{ width: 200 }}>
                <Form.Label column sm={6} style={{ textAlign: 'right' }}>
                  Page Size
                </Form.Label>
                <Col sm={6}>
                  <Form.Select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value))
                    }}>
                    {pageSizeOptions.map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </InputGroup>
          </ButtonToolbar>
        </div>
      }
    </div>
  )
}

export default BaseHtmlTable;